import cx from 'classnames';
import type { FunctionComponent, MouseEventHandler } from 'react';
import { Fragment, useCallback, useMemo, useRef } from 'react';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useFeatureGate } from '@trello/feature-gate-client';
import { optimisticIdManager } from '@trello/graphql';
import { useBoardId, useListId, useWorkspaceId } from '@trello/id-context';
import { useSharedStateSelector } from '@trello/shared-state';
import type { ListTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useBoardListsContext } from 'app/src/components/BoardListsContext';
import {
  getListCardsTreeWalker,
  getNodeCardId,
} from 'app/src/components/BoardListView/getListCardsTreeWalker';
import { useCanEditBoard } from 'app/src/components/BoardPermissionsContext';
import { openCardComposer } from 'app/src/components/CardComposer';
import { MemoizedPossibleCardComposer } from 'app/src/components/CardComposer/PossibleCardComposer';
import { visibleCardIdsSharedState } from 'app/src/components/FilterPopover/visibleCardIdsSharedState';
import { PinnedCardSeparator } from 'app/src/components/PinnedCardSeparator';
import { cardDragAndDropState } from './cardDragAndDropState';
import { getCardIdFromPoint } from './getCardIdFromPoint';
import { getCardPos } from './getCardPos';
import { MemoizedListCard } from './ListCard';
import { ListCardDropPreview } from './ListCardDropPreview';
import { ListCardGap } from './ListCardGap';
import { useAutoScrollListCards } from './useAutoScrollListCards';
import { useIncrementalIdleItemRenderer } from './useIncrementalIdleItemRenderer';
import { useIsListCollapsed } from './useListContext';

import styles from './ListCards.less';

// Keep this in sync with `@row-gap` in ListCards.less.
const VERTICAL_SPACE_BETWEEN_CARDS = 8;

let pageSize: number;

export const getPageSize = () => {
  if (!pageSize) {
    // Combined heights of global (48) + board (58) + list (46) headers.
    const unavailableVerticalSpace = 152;
    // Combined height of a card front with a one-line name (36).
    const minCardFrontHeight = 36 + VERTICAL_SPACE_BETWEEN_CARDS;
    const availableHeight = window.innerHeight - unavailableVerticalSpace;
    const rawPageSize = Math.ceil(availableHeight / minCardFrontHeight);
    pageSize = Math.max(rawPageSize, 15);
  }
  return pageSize;
};

interface ListCardsProps {
  className?: string;
}

export const ListCards: FunctionComponent<ListCardsProps> = ({ className }) => {
  const boardId = useBoardId();
  const listId = useListId();
  const workspaceId = useWorkspaceId();

  const cardsRef = useRef<HTMLOListElement>(null);
  const cards = useBoardListsContext(
    useCallback((value) => value.listCards?.[listId], [listId]),
  );
  const { value: isHoverInbetweenEnabled } = useFeatureGate(
    'trello_hover_inbetween_cards',
  );
  const isCollapsed = useIsListCollapsed();

  useAutoScrollListCards(cardsRef);

  useIncrementalIdleItemRenderer({
    queueId: listId,
    items: cards ?? [],
    pageSize: getPageSize(),
    isLoading: !cards,
  });

  const shouldShowDropPreview = useSharedStateSelector(
    cardDragAndDropState,
    useCallback(
      (state) => {
        // If dragging into the list but not over a card, position will be null:
        return state.currentListId === listId && state.currentPosition === null;
      },
      [listId],
    ),
  );

  const visibleCardIds = useSharedStateSelector(
    visibleCardIdsSharedState,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useCallback((state) => Array.from(state), [cards]),
  );
  const visibleCardIdsSet = useMemo(
    () => new Set(visibleCardIds),
    [visibleCardIds],
  );

  const { lastVisibleCardId, secondLastVisibleCardId } = useMemo(() => {
    if (!cards?.length) {
      return {
        lastVisibleCardId: undefined,
        secondLastVisibleCardId: undefined,
      };
    }

    let _lastVisibleCardId, _secondLastVisibleCardId;
    for (let i = cards?.length - 1; i >= 0; i--) {
      if (visibleCardIdsSet.has(cards[i].id)) {
        if (!_lastVisibleCardId) {
          _lastVisibleCardId = cards[i].id;
        } else if (!_secondLastVisibleCardId) {
          _secondLastVisibleCardId = cards[i].id;
          break;
        }
      }
    }

    return {
      lastVisibleCardId: _lastVisibleCardId,
      secondLastVisibleCardId: _secondLastVisibleCardId,
    };
  }, [cards, visibleCardIdsSet]);

  const movingCardId = useSharedStateSelector(
    cardDragAndDropState,
    useCallback((state) => {
      if (
        state.currentListId === state.originalListId &&
        state.currentPosition === state.originalPosition
      ) {
        return null;
      }
      return state.cardId;
    }, []),
  );

  const canEditBoard = useCanEditBoard();

  const openCardComposerInPosition = useCallback(
    (position: number) => {
      openCardComposer({ listId, position });
      Analytics.sendUIEvent({
        action: 'opened',
        actionSubject: 'cardComposer',
        source: 'boardScreen',
        attributes: { method: 'doubleClick' },
        containers: formatContainers({ boardId, listId, workspaceId }),
      });
    },
    [boardId, listId, workspaceId],
  );

  /**
   * This buffer is a small amount of padding above the first visible card that
   * can be double clicked to open the card composer at the top of the list.
   * It lives outside of the ordered list element because coupling this padding
   * to the scroll container would artificially pad the scrollbar.
   */
  const onDoubleClickTopBuffer: MouseEventHandler<HTMLDivElement> =
    useCallback(() => {
      if (isHoverInbetweenEnabled) {
        return;
      }

      if (!canEditBoard) {
        return;
      }
      openCardComposerInPosition(0);
    }, [canEditBoard, isHoverInbetweenEnabled, openCardComposerInPosition]);

  /**
   * Open the card composer between two cards when the gap is double clicked.
   * This method uses native DOM methods to attempt to identify the nearest card
   * above or below the event target, then open the card composer below or above
   * that card.
   *
   * This was originally achieved by applying padding to each card and attaching
   * double click handlers to each card, but managing conditional padding (e.g.
   * removing padding from the last visible card in a list) proved overly hard.
   * Using esoteric native DOM handlers here is a tradeoff to make the core path
   * more efficient, and to defer complexity behind user interaction.
   */
  const onDoubleClickCardsContainer: MouseEventHandler<HTMLOListElement> =
    useCallback(
      ({ clientX, clientY, target }) => {
        if (isHoverInbetweenEnabled) {
          return;
        }

        // Only handle clicks on the list container itself, not nested elements.
        if (!canEditBoard || target !== cardsRef.current) {
          return;
        }

        // Attempt to find the nearest card above the click target:
        const pointAbove = clientY - VERTICAL_SPACE_BETWEEN_CARDS;
        const cardIdFromPointAbove = getCardIdFromPoint(clientX, pointAbove);

        // In the happy path, we're able to find the card immediately above
        // the event target, and can simply open the card composer below it.
        if (cardIdFromPointAbove) {
          const position = getCardPos(cardIdFromPointAbove);
          if (typeof position !== 'undefined') {
            openCardComposerInPosition(position);
          }
          return;
        }

        // If we couldn't find a valid card ID above the click target, it's
        // possible that the card directly above is obscured by the scroll
        // container, so attempt to find the nearest card below:
        const pointBelow = clientY + VERTICAL_SPACE_BETWEEN_CARDS;
        const cardIdFromPointBelow = getCardIdFromPoint(clientX, pointBelow);

        // If we can't find a card above or below the target, something's wrong.
        // If we see this happening in live environments, we could track an
        // error and/or open the card composer at the bottom of the list;
        // for now, let's keep this flat and see how it feels.
        if (!cardIdFromPointBelow) {
          return;
        }

        // This is where things get a little complicated. If we know the card
        // _below_ the click target, we want to open the composer above it.
        // However, this isn't always as easy as subtracting its position by 1.
        // Because the card in the previous position might be hidden by filters,
        // we actually want the position of the previous _visible_ card above
        // the one we're clicking above, in order to anchor the card composer
        // under a currently visible card, rather than under a hidden card.
        const treeWalker = getListCardsTreeWalker(listId, cardIdFromPointBelow);
        const previousVisibleCardId = getNodeCardId(treeWalker.previousNode());
        if (!previousVisibleCardId) {
          return;
        }

        const position = getCardPos(previousVisibleCardId);
        if (typeof position !== 'undefined') {
          openCardComposerInPosition(position);
        }
      },

      [
        canEditBoard,
        isHoverInbetweenEnabled,
        listId,
        openCardComposerInPosition,
      ],
    );

  return (
    <>
      {!isHoverInbetweenEnabled && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className={styles.topBuffer}
          hidden={isCollapsed}
          onDoubleClick={onDoubleClickTopBuffer}
          data-testid={getTestId<ListTestIds>('list-cards-top-buffer')}
        />
      )}
      {cards?.length && movingCardId !== lastVisibleCardId && !isCollapsed ? (
        <ListCardGap
          listId={listId}
          prevPosition={-1}
          position={0}
          filtered={true}
          nextPosition={cards[0].pos}
        />
      ) : null}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <ol
        className={cx(styles.listCards, className, {
          [styles.hoverGapEnabled]: isHoverInbetweenEnabled,
        })}
        hidden={isCollapsed}
        ref={cardsRef}
        data-testid={getTestId<ListTestIds>('list-cards')}
        onDoubleClick={onDoubleClickCardsContainer}
      >
        <MemoizedPossibleCardComposer
          prevPosition={-1}
          position={0}
          nextPosition={cards?.length ? cards[0].pos : Infinity}
        />

        {cards?.map((card, index) => (
          <Fragment key={optimisticIdManager.getStableIdKey(card.id)}>
            <MemoizedListCard
              cardId={card.id}
              position={card.pos}
              filtered={visibleCardIdsSet.has(card.id)}
            />
            <MemoizedPossibleCardComposer
              prevPosition={cards[index - 1]?.pos ?? 0}
              position={card.pos}
              nextPosition={cards[index + 1]?.pos ?? Infinity}
            />

            {/* don't render gap if: 
            - it's the last in the list (the add card button is already on the bottom)
            - it corresponds to the moving card (double gap stacking)
            - it's the second last and the last card is moving (double gap stacking) */}
            {card.id === lastVisibleCardId ||
            card.id === movingCardId ||
            (lastVisibleCardId === movingCardId &&
              card.id === secondLastVisibleCardId) ? null : (
              <ListCardGap
                listId={listId}
                prevPosition={cards[index - 1]?.pos ?? 0}
                position={card.pos}
                nextPosition={cards[index + 1]?.pos ?? Infinity}
                filtered={visibleCardIdsSet.has(card.id)}
              />
            )}

            <PinnedCardSeparator
              listCards={cards}
              currentCard={card}
              index={index}
              filtered={visibleCardIdsSet.has(card.id)}
            />
          </Fragment>
        ))}

        {shouldShowDropPreview && (
          <ListCardDropPreview
            className={cx({
              [styles.lastDropPreview]: isHoverInbetweenEnabled,
            })}
          />
        )}
      </ol>
    </>
  );
};
